import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'

import { Header } from 'app/KinerjaShared/components/Header'
import { DatesSelector } from 'app/KinerjaHarian/components/forms/DatesSelector'
import { KinerjaLogForm } from 'app/KinerjaHarian/components/forms/KinerjaLogForm'
import { ConfirmationSection } from 'app/KinerjaHarian/components/forms/ConfirmationSection'
import { useAPIGetHolidayDates } from 'api/kinerja/holiday'
import { useSafeBackNavigation } from 'app/KinerjaShared/hooks/useSafeBackNavigation'
import DialogSavePostSendConfirmation from 'app/KinerjaHarian/components/forms/DialogSavePostSendConfirmation'
import { useKinerjaHarianTrackEvent } from 'app/KinerjaHarian/hooks/useKinerjaHarianTrackEvent'

import type {
  KinerjaHarianContent,
  KinerjaHarianFormProps,
  KinerjaHarianLog,
  RepeatingDateType,
  RequestUpsertKinerjaHarian,
} from 'types/kinerja-harian'

type Props = {
  basePath: string
  periodYear: number
  entryDate?: string

  contents: KinerjaHarianContent[]
  logsToEdit?: KinerjaHarianLog[]
  showConfirmationDialog?: boolean

  isSubmitting?: boolean
  onUpsert: (payload: RequestUpsertKinerjaHarian) => Promise<void>
}

export function UpsertKinerjaHarianLogsPage({
  basePath,
  entryDate,
  periodYear,
  logsToEdit,
  contents,
  showConfirmationDialog,
  isSubmitting = false,
  onUpsert,
}: Props) {
  const { trackEvent } = useKinerjaHarianTrackEvent()

  const { handleBack } = useSafeBackNavigation({
    fallbackRoute: basePath,
  })
  const [startDate, setStartDate] = useState<string | null>(entryDate || null)

  const editMode = !!entryDate

  const [repeatingDateType, setRepeatingDateType] =
    useState<RepeatingDateType>('non-repeating')

  const { data: holidayDates } = useAPIGetHolidayDates({
    params: {
      year: periodYear,
    },
    options: {
      staleTime: 60 * 60 * 1000, // 1 hour
      enabled: Boolean(periodYear),
      useErrorBoundary: false,
    },
  })

  const holidayDateSet = useMemo(
    // expected date format in API response is 'YYYY-MM-DD'
    () => new Set(holidayDates?.data?.holiday?.map((h) => h.date) ?? []),
    [holidayDates]
  )

  const [entryDates, setEntryDates] = useState<string[]>([])

  const [isConfirmed, setIsConfirmed] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const methods = useForm<KinerjaHarianFormProps>({
    mode: 'onBlur',
    defaultValues: {
      absent: false,
      contents: {},
    },
  })

  const formValues = methods.watch()
  const { reset, formState } = methods
  const isAbsentSelected = editMode && !!formValues.absent

  const isFormValid = useMemo(() => {
    if (entryDates.length === 0 || !formState.isValid) return false

    const checkedContents = Object.values(formValues.contents || {}).filter(
      (c) => c.isChecked
    )
    const areCheckedContentsValid = checkedContents.every(
      (c) => c.startTime && c.endTime
    )

    return (
      (isAbsentSelected || checkedContents.length > 0) &&
      areCheckedContentsValid
    )
  }, [entryDates, formValues, formState.isValid, isAbsentSelected])

  useEffect(() => {
    trackEvent({
      activityType: 'page_view',
      screenName: 'add',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Pre-fill form in edit mode
  useEffect(() => {
    const firstLog = logsToEdit?.[0] ?? null

    reset({
      absent: !!firstLog?.absent,
      contents: contents.reduce((acc, option) => {
        const existingContent = firstLog?.contents.find(
          (c) => c.contentId === option.id
        )

        acc[option.id] = {
          isChecked: !!existingContent,
          startTime: existingContent?.startHour || '',
          endTime: existingContent?.endHour || '',
        }

        return acc
      }, {} as KinerjaHarianFormProps['contents']),
    })
  }, [logsToEdit, contents, reset])

  const handleConfirmedSubmit = useCallback(() => {
    const formValues = methods.getValues()

    // Construct content array from checked items
    const contentsToBeSent = Object.entries(formValues.contents || {})
      .filter(([, value]) => value.isChecked)
      .map(([key, value]) => ({
        contentId: key,
        startHour: value.startTime,
        endHour: value.endTime,
      }))

    const payload = {
      entriesDate: entryDates,
      data: {
        contents: contentsToBeSent,
        absent: editMode ? !!formValues.absent : false,
      },
      eventType: editMode ? ('EDIT' as const) : ('UPSERT' as const),
    }

    trackEvent({
      activityType: 'click_save',
      screenName: 'add',
      additionalParams: {
        edit_type: editMode ? 'edit' : 'create',
      },
    })

    onUpsert(payload).finally(() => {
      setIsDialogOpen(false)
    })
  }, [methods, entryDates, editMode, trackEvent, onUpsert])

  const handleSubmit = useCallback(() => {
    if (showConfirmationDialog) {
      setIsDialogOpen(true)
    } else {
      handleConfirmedSubmit()
    }
  }, [showConfirmationDialog, handleConfirmedSubmit])

  return (
    <div
      aria-busy="false"
      className="flex items-center justify-center px-2 py-10"
    >
      <div className="w-full max-w-4xl mx-auto">
        <header>
          <Header title="Catat Kinerja Harian" onBack={handleBack} />
        </header>
        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(handleSubmit)}
            className="flex flex-col gap-5 mt-4"
          >
            <DatesSelector
              startDate={startDate}
              onChangeStartDate={setStartDate}
              repeatingDateType={repeatingDateType}
              onChangeRepeatingDateType={setRepeatingDateType}
              onChangeEntryDates={setEntryDates}
              holidayDates={holidayDateSet}
              disabled={editMode} // Disable date selection in edit mode
              minValue={`${periodYear}-01-01`}
              maxValue={`${periodYear}-12-31`}
              minYear={periodYear}
              maxYear={periodYear}
            />

            <KinerjaLogForm contents={contents} editMode={editMode} />

            <ConfirmationSection
              confirmationText={
                isAbsentSelected
                  ? 'Saya menyatakan tidak hadir pada tanggal di atas'
                  : 'Saya melaksanakan Kinerja Harian sesuai dengan data di atas'
              }
              isConfirmed={isConfirmed}
              onConfirmChange={setIsConfirmed}
            />

            <DialogSavePostSendConfirmation
              isOpen={isDialogOpen}
              isLoading={isSubmitting}
              onClose={() => setIsDialogOpen(false)}
              onConfirm={handleConfirmedSubmit}
            />

            <Button
              type="submit"
              variant="solid"
              color="blue"
              disabled={!isConfirmed || !isFormValid || isSubmitting}
              isLoading={isSubmitting}
              fullWidth
            >
              {editMode && (
                <Icon className="mr-1" color="inherit">
                  edit_outline
                </Icon>
              )}
              Simpan
            </Button>
          </form>
        </FormProvider>
      </div>
    </div>
  )
}
